import PropTypes from 'prop-types';
import React from 'react';
import { Button, Card } from 'ui';
import useChatbotPracticeSummary from '../../hooks/useChatbotPracticeSummary';

const ChatbotPracticeSummary = ({ firstName, activityResults = [], sessionKCs = [], lessonTitle, onReview, isReviewGenerating = false }) => {
  const { title, skillsTitle, answersTitle, robotModifier, robotLabel, reviewPrompt, reviewCta, reviewRobotLabel } = useChatbotPracticeSummary({
    activityResults,
    firstName,
  });

  // Offer a review practice only when at least one worked KC still needs improvement (TANAI-1827).
  const canReview = typeof onReview === 'function' && sessionKCs.some((kc) => kc.needsImprovement);

  return (
    <div className="ai-practice-view__summary">
      <Card padding="md">
        <div className="ai-practice-view__lesson-title">
          <div className="ai-practice-view__lesson-title-text" dangerouslySetInnerHTML={{ __html: lessonTitle }} />
        </div>
        <div className="ai-practice-view__summary-content">
          {/* Upper section with padding */}
          <div className="ai-practice-view__summary-upper-section">
            {/* Title + state robot */}
            <div className="ai-practice-view__summary-celebration">
              <h2 className="ai-practice-view__summary-title">{title}</h2>
              <div
                aria-label={robotLabel}
                className={`ai-practice-view__summary-celebration-image ai-practice-view__summary-celebration-image--${robotModifier}`}
                role="img"
              />
            </div>

            {/* Skills worked in the session — always shown; only the header is reframed by state */}
            {sessionKCs.length > 0 && (
              <div className="ai-practice-view__summary-learning-sections">
                <div className="ai-practice-view__summary-section">
                  <h3 className="ai-practice-view__summary-section-title">{skillsTitle}</h3>
                  <ul className="ai-practice-view__summary-list">
                    {sessionKCs.map((kc, index) => (
                      <li key={kc.referenceId ?? index} className="ai-practice-view__summary-list-item">
                        {kc.name}.
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

          </div>

          {/* Separator */}
          <div className="ai-practice-view__summary-separator"></div>

          {/* Tus respuestas section */}
          <div className="ai-practice-view__summary-answers-section">
            <h3 className="ai-practice-view__summary-answers-title">{answersTitle}</h3>
            <div className="ai-practice-view__summary-responses">
              {activityResults.map((result, index) => (
                <div
                  key={result.activityInstanceId ?? index}
                  className={`ai-practice-view__summary-response-indicator ${
                    result.isCorrect ? 'ai-practice-view__summary-response-indicator--correct' : 'ai-practice-view__summary-response-indicator--incorrect'
                  }`}
                >
                  <span className="ai-practice-view__summary-response-number">{index + 1}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Review practice card — below the summary, only when a worked skill still needs improvement */}
      {canReview && (
        <Card className="ai-practice-view__summary-review-card">
          <div className="ai-practice-view__summary-review">
            <div className="ai-practice-view__summary-review-message">
              <div aria-label={reviewRobotLabel} className="ai-practice-view__summary-review-avatar" role="img" />
              <p className="ai-practice-view__summary-review-text">{reviewPrompt}</p>
            </div>
            <Button variant="primary" text={reviewCta} onClick={onReview} isLoading={isReviewGenerating} size="sm" />
          </div>
        </Card>
      )}
    </div>
  );
};

ChatbotPracticeSummary.propTypes = {
  firstName: PropTypes.string,
  activityResults: PropTypes.arrayOf(
    PropTypes.shape({
      isCorrect: PropTypes.bool,
      response: PropTypes.object,
      activityInstanceId: PropTypes.string,
      associatedKC: PropTypes.string,
    })
  ),
  sessionKCs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      score: PropTypes.number,
      referenceId: PropTypes.string,
      needsImprovement: PropTypes.bool,
    })
  ),
  lessonTitle: PropTypes.string,
  onReview: PropTypes.func,
  isReviewGenerating: PropTypes.bool,
};

export default ChatbotPracticeSummary;
